/**
 * PortsEditor — repeatable port list used by step 2 (inputs) and
 * step 3 (outputs). Uses `useFieldArray` from react-hook-form so
 * ordering + add/remove happen outside the component's state.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useFieldArray, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { cn } from '@/utils/cn';

import { inputClass, Field } from '../steps/Step1Basic';
import type { PortValues } from '../schemas';

const GI_TYPES = ['Stream', 'Event', 'Document', 'Signal', 'Tensor', 'Value', 'Binary'] as const;

// The ports editor is reused across Step 2 (inputs) and Step 3
// (outputs) — both shapes are structurally identical. Rather than
// plumbing two distinct generics through, we accept `Control<any>`
// and trust the callers to pass `name: 'inputs' | 'outputs'` matching
// their own form shape. `useFieldArray` is runtime-keyed on `name`,
// so this is safe as long as each caller's form has a field of that
// name typed as `PortValues[]`.
export function PortsEditor({
  control,
  register,
  errors,
  name,
}: {
  control: Control<any>;
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  name: 'inputs' | 'outputs';
}) {
  const { t } = useTranslation('spark');
  const { fields, append, remove } = useFieldArray({ control, name });
  const portErrors = (errors[name] ?? []) as FieldErrors<PortValues>[];

  return (
    <div className="flex flex-col gap-3">
      {fields.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-surface p-4 text-sm text-ink-muted">
          {t('ports.empty')}
        </p>
      ) : null}

      {fields.map((f, i) => {
        const fieldErr = portErrors[i] ?? {};
        return (
          <div
            key={f.id}
            className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-surface p-4 md:grid-cols-[1fr_180px_100px_auto]"
          >
            <Field
              label={t('ports.field_name')}
              error={fieldErr.name?.message}
            >
              <input
                type="text"
                placeholder={t('ports.name_placeholder')}
                {...register(`${name}.${i}.name` as const)}
                className={inputClass(Boolean(fieldErr.name))}
              />
            </Field>

            <Field
              label={t('ports.field_type')}
              error={fieldErr.data_type?.message}
            >
              <select
                {...register(`${name}.${i}.data_type` as const)}
                className={inputClass(Boolean(fieldErr.data_type))}
              >
                {GI_TYPES.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('ports.field_required')}>
              <label className="flex h-10 items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm">
                <input
                  type="checkbox"
                  {...register(`${name}.${i}.required` as const)}
                  className="h-4 w-4 accent-spark"
                />
                <span className="text-ink-muted">
                  {t('ports.required_hint')}
                </span>
              </label>
            </Field>

            <div className="flex items-end">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => remove(i)}
                aria-label={t('ports.remove', { index: i + 1 })}
              >
                {t('ports.remove_short')}
              </Button>
            </div>

            <div className="md:col-span-4">
              <Field
                label={t('ports.field_schema')}
                hint={t('ports.schema_hint')}
                error={fieldErr.schema_text?.message}
              >
                <textarea
                  rows={3}
                  placeholder='{"type":"object"}'
                  {...register(`${name}.${i}.schema_text` as const)}
                  className={cn(inputClass(Boolean(fieldErr.schema_text)), 'font-mono text-xs')}
                />
              </Field>
            </div>
          </div>
        );
      })}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() =>
          append({
            name: '',
            data_type: 'Document',
            required: true,
            schema_text: '',
          })
        }
      >
        {t('ports.add')}
      </Button>
    </div>
  );
}
